import type { DeliveryBoardOrderDto, DeliveryStaffDto } from '@matix/api-client';

/**
 * Shared model for the Delivery Manager — the legacy POS board
 * `/Delivery/Manager` (مدير التوصيل). Statuses are `mstr_DeliveryStatus`;
 * the server owns the workflow (deliveryManager.rules.ts in pos-api), this
 * file only decides what to SHOW.
 */

export const STATUS = {
  New: 0,
  Ready: 1,
  Waiting: 2,
  OnTheWay: 3,
  Delivered: 4,
  Returned: 5,
} as const;

export const ORDER_TYPE = { TakeAway: 1, Delivery: 2, DineIn: 3, Pickup: 4 } as const;

export type TabKey = 'new' | 'ready' | 'waiting' | 'onTheWay' | 'delivered' | 'returned' | 'timeline';

export interface TabMeta {
  key: TabKey;
  status: number | null;
  en: string;
  ar: string;
  tone: 'green' | 'amber' | 'blue' | 'violet' | 'slate' | 'rose' | 'plain';
}

/** Same tabs, same order as the legacy page. */
export const TABS: TabMeta[] = [
  { key: 'new', status: STATUS.New, en: 'New', ar: 'جديد', tone: 'green' },
  { key: 'ready', status: STATUS.Ready, en: 'Ready', ar: 'جاهز', tone: 'amber' },
  { key: 'waiting', status: STATUS.Waiting, en: 'Waiting for pickup', ar: 'بانتظار الاستلام', tone: 'blue' },
  { key: 'onTheWay', status: STATUS.OnTheWay, en: 'Delivery in progress', ar: 'جاري التوصيل', tone: 'violet' },
  { key: 'delivered', status: STATUS.Delivered, en: 'Delivered', ar: 'تم التوصيل', tone: 'slate' },
  { key: 'returned', status: STATUS.Returned, en: 'Returned', ar: 'مرتجع', tone: 'rose' },
  { key: 'timeline', status: null, en: 'Timeline', ar: 'الخط الزمني', tone: 'plain' },
];

export const STATUS_LABEL: Record<number, { en: string; ar: string }> = {
  0: { en: 'New', ar: 'جديد' },
  1: { en: 'Ready', ar: 'جاهز' },
  2: { en: 'Waiting for pickup', ar: 'بانتظار الاستلام' },
  3: { en: 'Delivery in progress', ar: 'جاري التوصيل' },
  4: { en: 'Delivered', ar: 'تم التوصيل' },
  5: { en: 'Returned', ar: 'مرتجع' },
  6: { en: 'Voided', ar: 'ملغي' },
  7: { en: 'Closed', ar: 'مغلق' },
};

export const ORDER_TYPE_LABEL: Record<number, { en: string; ar: string }> = {
  1: { en: 'Take away', ar: 'سفري' },
  2: { en: 'Delivery', ar: 'توصيل' },
  3: { en: 'Dine in', ar: 'محلي' },
  4: { en: 'Pickup', ar: 'استلام' },
};

// ── Wall clock ──────────────────────────────────────────────────────────────
//
// Every timestamp on this board is a NAIVE tenant wall clock. The API sends
// `Entry_Date`-style values as digits + 'Z' (the Z does not mean UTC) and
// `now` as bare digits. Reading both as UTC puts them on ONE axis without ever
// touching the browser's time zone — a till whose PC clock or zone is wrong
// still shows the right elapsed and remaining times.

export function wallMs(v: string | null | undefined): number | null {
  if (!v) return null;
  const ms = Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(v) ? v : `${v}Z`);
  return Number.isNaN(ms) ? null : ms;
}

/** "1:05:09" / "2d 1:05:09" — the legacy D:H:M:S, tidied. */
export function formatSpan(ms: number): string {
  const total = Math.max(0, Math.floor(Math.abs(ms) / 1000));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const hms = `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return d > 0 ? `${d}d ${hms}` : hms;
}

/** "22 Sep 18:00" from a wall-clock value, locale-neutral digits. */
export function formatStamp(v: string | null | undefined, lang: 'en' | 'ar'): string {
  const ms = wallMs(v);
  if (ms == null) return '—';
  const d = new Date(ms);
  const months = lang === 'ar'
    ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
    : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${hh}:${mm}`;
}

export type Urgency = 'late' | 'soon' | 'ok';

/**
 * The legacy row colours: within 15 minutes of the promised time → warning;
 * past it → late. A delivered or returned order is never "late" any more.
 */
export function urgencyOf(o: DeliveryBoardOrderDto, nowMs: number): Urgency {
  if (o.status >= STATUS.Delivered) return 'ok';
  const due = wallMs(o.delivery_date) ?? wallMs(o.entry_date);
  if (due == null) return 'ok';
  if (nowMs > due) return 'late';
  if (due - nowMs <= 15 * 60 * 1000) return 'soon';
  return 'ok';
}

/** Minutes spent in each step — the legacy `CreateDeliveryHistory`, from Entry_Date. */
export function timelineSteps(o: DeliveryBoardOrderDto): Array<{ status: number; minutes: number }> {
  let prev = wallMs(o.entry_date);
  const steps: Array<{ status: number; minutes: number }> = [];
  for (const h of o.history) {
    const at = wallMs(h.at);
    if (at == null) continue;
    steps.push({ status: h.status, minutes: prev == null ? 0 : Math.max(0, (at - prev) / 60000) });
    prev = at;
  }
  return steps;
}

// ── Names ───────────────────────────────────────────────────────────────────

export function pick(lang: 'en' | 'ar', en: string | null | undefined, ar: string | null | undefined): string {
  return (lang === 'ar' ? ar || en : en || ar) || '';
}

export function customerOf(o: DeliveryBoardOrderDto, lang: 'en' | 'ar'): string {
  return pick(lang, o.customer_name, o.customer_name_ar);
}

export function staffName(s: DeliveryStaffDto | null | undefined, lang: 'en' | 'ar'): string {
  return s ? pick(lang, s.name, s.name_ar) : '';
}

export function areaOf(o: DeliveryBoardOrderDto, lang: 'en' | 'ar'): string {
  const area = pick(lang, o.area_name, o.area_name_ar);
  const block = o.block ? `${lang === 'ar' ? 'قطعة' : 'Block'} ${o.block}` : '';
  return [area, block].filter(Boolean).join(' - ');
}

export function orderNo(o: DeliveryBoardOrderDto): string {
  return `#${o.entry_no ?? o.id}`;
}

/** Does this search text match the order (number, customer, phone, driver)? */
export function matches(o: DeliveryBoardOrderDto, q: string): boolean {
  const s = q.trim().toLowerCase().replace(/^#/, '');
  if (!s) return true;
  return [
    String(o.entry_no ?? ''),
    o.customer_name,
    o.customer_name_ar,
    o.customer_phone,
    o.driver_name,
    o.driver_name_ar,
    o.area_name,
    o.area_name_ar,
  ].some((v) => v != null && String(v).toLowerCase().includes(s));
}
